import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, effect, inject, input, viewChild } from '@angular/core';
import { LOGGER } from '../hgm-setup/src/module/util/logger';

@Component({
    selector: 'line-sample',
    template: `<canvas #canvas></canvas>`,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        :host {
            display: inline-flex;
            justify-content: center;
            align-items: center;
        }
        canvas {
            display: block;
            border: 1px solid rgba(0, 0, 0, 0.1);
        }
    `,
})
export class LineSampleComponent implements AfterViewInit, OnDestroy {
    color = input<string>();
    dash = input<number[]>([]);
    lineWidth = input(3);
    scale = input(1);
    width = input(36);
    height = input(12);

    private canvas = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
    private logger = inject(LOGGER);

    constructor() {
        // Redraw whenever signals change internally
        effect(() => {
            const width = this.width();
            const height = this.height();
            const scale = this.scale();
            const color = this.color();
            const dash = this.dash();
            const lineWidth = this.lineWidth();
            const canvasRef = this.canvas();

            // Only attempt drawing if the element natively arrived
            if (canvasRef) {
                setTimeout(() => this.draw(canvasRef, width, height, scale, color, dash, lineWidth), 0);
            }
        });
    }

    ngAfterViewInit() {
        this.draw(this.canvas(), this.width(), this.height(), this.scale(), this.color(), this.dash(), this.lineWidth());
    }

    ngOnDestroy() {
        if (this.canvas()?.nativeElement) {
            this.canvas()!.nativeElement.parentElement?.removeChild(this.canvas()!.nativeElement);
        }
    }

    private draw(
        canvasRef: ElementRef<HTMLCanvasElement> | undefined,
        width: number,
        height: number,
        scale: number,
        color: string | undefined,
        dash: number[],
        lineWidth: number,
    ) {
        const canvas = canvasRef?.nativeElement;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        try {
            // Explicitly set the CSS pixel dimensions so the canvas cannot collapse to zero
            canvas.style.width = width + 'px';
            canvas.style.height = height + 'px';

            // Scale internal rendering buffer for sharp high-DPI retina displays
            const dpr = window.devicePixelRatio || 1;
            canvas.width = width * dpr;
            canvas.height = height * dpr;

            ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, width, height);

            if (color) ctx.strokeStyle = color;

            const dashArray = dash || [];
            ctx.setLineDash(dashArray.map((l) => l * (scale || 1)));
            ctx.lineWidth = lineWidth || 3;
            ctx.beginPath();
            ctx.moveTo(0, height / 2);
            ctx.lineTo(width, height / 2);
            ctx.stroke();
        } catch (e: any) {
            this.logger.error('LineSampleComponent draw error:', e);
        }
    }
}
